import { Button, Popconfirm, Skeleton, Space, Typography } from 'antd'
import { useState } from 'react'
import { useDeleteStatementMutation, useMarkStatementNotPaidManuallyMutation, useStatementQuery } from '../../../domain/invoicing/statementsQueries'
import { InvoiceSummaryWithCustomerJobData } from '../../../domain/invoicing/invoice'
import { centsToDollarsDisplay } from '../../displayFormatters'
import VSpace from '../../common/VSpace'
import { splitStatementInvoices } from './statementDetailModel'
import { collectedSurchargeLines } from './collectedSurchargeModel'
import StatementPaymentModal from '../PayInvoice/StatementPaymentModal'
import SendStatementModal from '../SendInvoice/SendStatementModal'
import EmailCustomerStatementModal from '../SendInvoice/EmailCustomerStatementModal'
import { markNotPaidConfirmation, statementActionAffordances } from './statementActionsModel'

const { Text } = Typography

type Props = {
  statementId: string
  currentInvoiceId: string
}

const StatementInvoiceDetails = ({ statementId, currentInvoiceId }: Props) => {
  const statementQuery = useStatementQuery(statementId)
  const deleteMutation = useDeleteStatementMutation()
  const notPaidMutation = useMarkStatementNotPaidManuallyMutation()
  const [showPayment, setShowPayment] = useState(false)
  const [showSend, setShowSend] = useState(false)
  const [showReminder, setShowReminder] = useState(false)

  if (statementQuery.isLoading) {
    return <Skeleton active />
  }
  if (!statementQuery.data) {
    return null
  }

  const { statement, invoices } = statementQuery.data
  const { thisJob, otherJobs } = splitStatementInvoices(invoices, currentInvoiceId)
  const affordances = statementActionAffordances(statement)
  const notPaidConfirmation = markNotPaidConfirmation(statement)

  const handleSend = () => {
    if (affordances.sendIsReminder) {
      setShowReminder(true)
    } else {
      setShowSend(true)
    }
  }

  return <div style={{ padding: '0 38px' }}>
    <VSpace>
      <div>
        <Text type="secondary" strong>THIS JOB</Text>
        {thisJob && <InvoiceLine invoice={thisJob} />}
      </div>
      <div>
        <Text type="secondary" strong>OTHER JOBS ON THIS STATEMENT ({otherJobs.length})</Text>
        {otherJobs.map(invoice => <InvoiceLine key={invoice.invoiceId} invoice={invoice} />)}
      </div>
      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, textAlign: 'right' }}>
        <div>Total: {centsToDollarsDisplay(statement.total)}</div>
        {collectedSurchargeLines(statement).map(line =>
          <div key={line.key}>{line.label}: {centsToDollarsDisplay(line.amount)}</div>
        )}
        <div>Balance: {centsToDollarsDisplay(statement.balance)}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <Space>
          {affordances.canSend && (
            <Button onClick={handleSend}>{affordances.sendLabel}</Button>
          )}
          {affordances.canPay && (
            <Button type="primary" onClick={() => setShowPayment(true)}>Pay Statement</Button>
          )}
          {affordances.canMarkNotPaid && (
            <Popconfirm
              title={notPaidConfirmation.title}
              description={notPaidConfirmation.description}
              okText="Yes"
              cancelText="No"
              onConfirm={() => notPaidMutation.mutate(statementId)}
            >
              <Button disabled={notPaidMutation.isLoading} loading={notPaidMutation.isLoading}>Mark Statement Not Paid</Button>
            </Popconfirm>
          )}
          <Popconfirm
            title={`Delete statement ${statement.statementNumber}?`}
            description={`This deletes the statement and its ${statement.invoiceCount} invoices.`}
            okText="Delete"
            okButtonProps={{ danger: true }}
            disabled={!affordances.canDelete}
            onConfirm={() => deleteMutation.mutate({ statementId, deleteFromQuickBooks: false })}
          >
            <Button danger disabled={!affordances.canDelete || deleteMutation.isLoading} loading={deleteMutation.isLoading}>Delete Statement</Button>
          </Popconfirm>
        </Space>
      </div>
    </VSpace>
    <StatementPaymentModal
      isOpen={showPayment}
      onClose={() => setShowPayment(false)}
      statementId={statementId}
      customerId={statement.customerId}
      statementTotal={statement.total}
    />
    {showSend && <SendStatementModal statement={statement} onClose={() => setShowSend(false)} />}
    <EmailCustomerStatementModal
      isOpen={showReminder}
      onClose={() => setShowReminder(false)}
      statement={statement}
    />
  </div>
}

const InvoiceLine = ({ invoice }: { invoice: InvoiceSummaryWithCustomerJobData }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
    <Text>{invoice.jobName}</Text>
    <Text>{centsToDollarsDisplay(invoice.total)}</Text>
  </div>
)

export default StatementInvoiceDetails
